import React from 'react';
import { motion } from 'framer-motion';
import { useModal } from '../services/ModalService';
import '../styles/pages/CloudSolutions.css';

const CloudSolutions = () => {
  const { openContactModal } = useModal();

  return (
    <div className="cloud-page">
      {/* Hero Section */}
      <section className="cloud-hero">
        <div className="cloud-container">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            style={{ textAlign: "center" }}
          >
            <div className="hero-badge">
              ☁️ Cloud Transformation
            </div>

            <h1 className="hero-title"
            dangerouslySetInnerHTML={{ __html: "Scalable <span class=\"gradient-text\">Cloud Solutions</span><br />for Modern Business" }}
            />

            <p className="hero-subtitle">
              Transform your business infrastructure with cutting-edge cloud solutions. Our expert team delivers reliable, scalable, and cost-effective cloud strategies tailored to your needs.
            </p>

            <div className="hero-buttons">
              <button
                className="btn-primary"
                onClick={() => openContactModal(
                  'Cloud Solutions Services',
                  'Cloud Solutions Page - Start Migration'
                )}
              >
                Start Migration
              </button>
              <button
                className="btn-secondary"
                onClick={() => openContactModal(
                  'Cloud Assessment Request',
                  'Cloud Solutions Page - Cloud Assessment'
                )}
              >
                Cloud Assessment
              </button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Services Section */}
      <section className="cloud-section">
        <div className="cloud-container">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <h2 className="cloud-section-title gradient-text">Cloud Infrastructure Services</h2>
            <p className="cloud-section-subtitle">
              Comprehensive cloud solutions to modernize your infrastructure and accelerate business growth
            </p>

            <div style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(350px, 1fr))",
              gap: "30px",
              marginTop: "60px"
            }}>

              <div className="cloud-glass-card">
                <h3 style={{ color: "#8B5CF6", marginBottom: "20px", fontSize: "1.5rem" }}>Cloud Migration</h3>
                <p style={{ marginBottom: "20px", lineHeight: "1.6" }}>Seamless migration of your applications and data to the cloud with minimal downtime and maximum reliability.</p>
                <ul style={{ listStyle: "none", padding: 0, color: "#666" }}>
                  <li style={{ marginBottom: "8px" }}>✓ Assessment & Planning</li>
                  <li style={{ marginBottom: "8px" }}>✓ Application Migration</li>
                  <li style={{ marginBottom: "8px" }}>✓ Data Migration Services</li>
                  <li style={{ marginBottom: "8px" }}>✓ Post-Migration Support</li>
                </ul>
              </div>

              <div className="cloud-glass-card">
                <h3 style={{ color: "#8B5CF6", marginBottom: "20px", fontSize: "1.5rem" }}>Multi-Cloud Architecture</h3>
                <p style={{ marginBottom: "20px", lineHeight: "1.6" }}>Design and implement robust multi-cloud strategies across AWS, Azure, and Google Cloud platforms.</p>
                <ul style={{ listStyle: "none", padding: 0, color: "#666" }}>
                  <li style={{ marginBottom: "8px" }}>✓ AWS Solutions</li>
                  <li style={{ marginBottom: "8px" }}>✓ Microsoft Azure</li>
                  <li style={{ marginBottom: "8px" }}>✓ Google Cloud Platform</li>
                  <li style={{ marginBottom: "8px" }}>✓ Hybrid Cloud Solutions</li>
                </ul>
              </div>

              <div className="cloud-glass-card">
                <h3 style={{ color: "#8B5CF6", marginBottom: "20px", fontSize: "1.5rem" }}>Serverless Computing</h3>
                <p style={{ marginBottom: "20px", lineHeight: "1.6" }}>Build and deploy applications using serverless architectures for improved scalability and cost efficiency.</p>
                <ul style={{ listStyle: "none", padding: 0, color: "#666" }}>
                  <li style={{ marginBottom: "8px" }}>✓ Function-as-a-Service</li>
                  <li style={{ marginBottom: "8px" }}>✓ Event-Driven Architecture</li>
                  <li style={{ marginBottom: "8px" }}>✓ Microservices Design</li>
                  <li style={{ marginBottom: "8px" }}>✓ Auto-scaling Solutions</li>
                </ul>
              </div>

              <div className="cloud-glass-card">
                <h3 style={{ color: "#8B5CF6", marginBottom: "20px", fontSize: "1.5rem" }}>Cloud Optimization</h3>
                <p style={{ marginBottom: "20px", lineHeight: "1.6" }}>Ongoing cost, performance, and capacity optimization for cloud workloads.</p>
                <ul style={{ listStyle: "none", padding: 0, color: "#666" }}>
                  <li style={{ marginBottom: "8px" }}>✓ Cost Analysis</li>
                  <li style={{ marginBottom: "8px" }}>✓ Resource Rightsizing</li>
                  <li style={{ marginBottom: "8px" }}>✓ Performance Tuning</li>
                  <li style={{ marginBottom: "8px" }}>✓ Capacity Planning</li>
                </ul>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="cloud-section">
        <div className="cloud-container">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
          >
            <h2 className="cloud-section-title gradient-text">Cloud Advantages</h2>
            <p className="cloud-section-subtitle">
              Experience the benefits of modern cloud infrastructure for your business
            </p>

            <div style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
              gap: "30px",
              marginTop: "60px"
            }}>

              <div className="cloud-glass-card" style={{ textAlign: "center" }}>
                <div style={{ fontSize: "2.5rem", marginBottom: "15px" }}>🚀</div>
                <h4 style={{ color: "#8B5CF6", marginBottom: "10px", fontSize: "1.2rem" }}>Scalability</h4>
                <p style={{ color: "#666", lineHeight: "1.5" }}>Scale resources up or down automatically based on demand</p>
              </div>

              <div className="cloud-glass-card" style={{ textAlign: "center" }}>
                <div style={{ fontSize: "2.5rem", marginBottom: "15px" }}>💰</div>
                <h4 style={{ color: "#8B5CF6", marginBottom: "10px", fontSize: "1.2rem" }}>Cost Efficiency</h4>
                <p style={{ color: "#666", lineHeight: "1.5" }}>Pay only for what you use with optimized resource allocation</p>
              </div>

              <div className="cloud-glass-card" style={{ textAlign: "center" }}>
                <div style={{ fontSize: "2.5rem", marginBottom: "15px" }}>⚙️</div>
                <h4 style={{ color: "#8B5CF6", marginBottom: "10px", fontSize: "1.2rem" }}>Operational Reliability</h4>
                <p style={{ color: "#666", lineHeight: "1.5" }}>Resilient architectures with proactive monitoring and recovery planning</p>
              </div>

              <div className="cloud-glass-card" style={{ textAlign: "center" }}>
                <div style={{ fontSize: "2.5rem", marginBottom: "15px" }}>🌐</div>
                <h4 style={{ color: "#8B5CF6", marginBottom: "10px", fontSize: "1.2rem" }}>Global Reach</h4>
                <p style={{ color: "#666", lineHeight: "1.5" }}>Deploy applications globally with low latency and high availability</p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cloud-section">
        <div className="cloud-container">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6 }}
          >
            <div className="cloud-cta-card">
              <h3 style={{
                fontSize: "2rem",
                marginBottom: "20px",
                color: "#8B5CF6"
              }}>
                Ready to Move to the Cloud?
              </h3>
              <p style={{
                fontSize: "1.1rem",
                marginBottom: "30px",
                color: "#666",
                maxWidth: "600px",
                margin: "0 auto 30px"
              }}>
                Start your cloud transformation journey today. Our experts will help you design and implement the perfect cloud strategy for your business.
              </p>
              <div style={{ display: "flex", gap: "20px", justifyContent: "center", flexWrap: "wrap" }}>
                <button
                  className="btn-primary"
                  onClick={() => openContactModal(
                    'Cloud Migration Project',
                    'Cloud Solutions Page - Begin Migration'
                  )}
                >
                  Begin Migration
                </button>
                <button
                  className="btn-secondary"
                  onClick={() => openContactModal(
                    'Free Cloud Assessment',
                    'Cloud Solutions Page - Free Cloud Assessment'
                  )}
                >
                  Free Cloud Assessment
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default CloudSolutions;
